import { cx } from '@emotion/css';
import { ArrowForwardRounded, PlayArrowRounded } from '@mui/icons-material';
import { Divider } from '@sortlist-frontend/design-system/legacy';
import { useTranslation } from '@sortlist-frontend/translation/ssr';
import { FC, Fragment } from 'react';

import * as S from '_components/common/StoryCard/styles';
import { onImageError } from '_core/media/get-media.utils';
import { customConfig } from '_features/custom/custom.config';
import { LinkStoryDetails } from '_features/story/utils';
import { LinkStoriesDetails } from '_types/generic';

type StoryCardCardProps = {
  isHeroCard?: boolean;
  isNewStory?: boolean;
  relatedStories?: LinkStoriesDetails[];
  className?: string | undefined;
  // TOFIX: It should be done in one prop but Payload do not match for stories/story for the same type of data, we should change that.
  story?: LinkStoriesDetails['metadata'] | LinkStoryDetails;
  linkStories?: LinkStoriesDetails;
  linkStory?: LinkStoryDetails;
  link?: string;
  target?: '_blank' | '_self';
};

const defaultStoryCardCardProps = {
  target: '_self',
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const StoryCard: FC<StoryCardCardProps> = (props) => {
  const { isHeroCard, className, linkStories, linkStory, link, isNewStory, story, target } = {
    ...defaultStoryCardCardProps,
    ...props,
  };
  const { t } = useTranslation(customConfig.i18nNamespaces);
  const isAgency: boolean = linkStories
    ? linkStories?.metadata?.business?.type === 'agency'
    : linkStory?.business.type === 'agency';
  const business = linkStories ? linkStories?.metadata?.business : linkStory?.business;
  const person = linkStories ? linkStories?.metadata?.persons?.[0] : linkStory?.persons;
  const thumbnail = linkStories ? linkStories?.metadata?.thumbnailUrl : linkStory?.thumbnailUrl;

  if (!!linkStories || !!linkStory || !!story)
    return (
      <a href={linkStories?.url || ''} target={target}>
        <S.Shape
          isHeroCard={isHeroCard}
          className={cx(
            className,
            isHeroCard ? 'border-primary-500 layout-row shadow-4' : 'border-secondary-300 layout-column shadow-2',
            linkStories?.url && 'cursor-pointer',
            'bg-neutral-50',
          )}>
          <section
            data-testid="story-card"
            className={cx(
              isHeroCard ? 'flex-order-0 flex-gt-sm-60 p-8 layout-column layout-align-center-start' : 'flex-order-1',
            )}>
            <div className={cx(isHeroCard ? 'mb-8' : 'p-4 flex-1', 'layout-column')}>
              <div className={cx(isHeroCard ? 'flex-order-0' : 'flex-order-1', 'layout-row')}>
                {isNewStory && (
                  <span className="flex-order-0 bg-primary-200 text-primary-500 py-1 px-3 small rounded-sm text-capitalize mr-2">
                    {t('stories:hero.position')}
                  </span>
                )}
                {isAgency && (
                  <span className="bg-supply-200 text-supply-700 py-1 px-3 small rounded-sm text-capitalize mr-2">
                    {t('stories:type.agency')}
                  </span>
                )}
                {!isAgency && (
                  <span className="bg-demand-200 text-demand-700 py-1 px-3 small rounded-sm text-capitalize mr-2">
                    {t('stories:type.client')}
                  </span>
                )}
              </div>
              <div className={cx(isHeroCard ? 'flex-order-1' : 'flex-order-0 mb-2', 'layout-column')}>
                <S.Title isHeroCard={isHeroCard} className="mb-2 bold text-secondary-900">
                  {business?.name}
                </S.Title>
                <span
                  className={cx(
                    isHeroCard ? 'h5' : 'small',
                    'bold text-secondary-900',
                  )}>{`${person?.firstName} ${person?.lastName}`}</span>
                <span className={cx(isHeroCard ? 'p' : 'small', 'text-secondary-500 lh-2')}>{person?.jobTitle}</span>
              </div>
            </div>
            {!isHeroCard && linkStories && <Divider />}
            {linkStories && (
              <footer className="layout-column layout-align-start-stretch">
                <span
                  role="button"
                  className={cx(
                    isHeroCard
                      ? 'btn btn-raised btn-primary btn-lg'
                      : 'layout-row layout-align-start-center flex-1 px-4 py-6 text-secondary-900 flex-1',
                  )}>
                  <span className="flex-1">{t('stories:hero.testimonial')}</span>
                  {!isHeroCard && <ArrowForwardRounded style={{ fontSize: 16 }} />}
                </span>
              </footer>
            )}
          </section>
          <section className={cx(isHeroCard ? ' flex-order-1' : ' flex-order-0', 'flex-1 relative p-2 flex-1')}>
            {linkStories?.metadata?.has_video && (
              <S.PlayIcon
                isHeroCard={isHeroCard}
                className="text-secondary-700 rounded-full layout-row layout-align-center-center">
                <PlayArrowRounded style={{ fontSize: isHeroCard ? 24 : 20 }} />
              </S.PlayIcon>
            )}
            <img src={thumbnail} alt={business?.name} onError={onImageError} className="width-100 rounded-xl" />
          </section>
        </S.Shape>
      </a>
    );

  return <Fragment></Fragment>;
};
